import React from 'react';

import {
  formatClock,
  RunPlan,
  runStepStatuses,
} from 'components/ExportDesign/exportDesignRunPanelData';

// The live run panel (UX slice S4, board 02): while a turn works, its plan for the capability the
// router picked, each step done / active / pending / skipped, the latest lines under the active step,
// and how long it usually takes. When the turn ends the chat folds it into the usual "Thought for Ns"
// log. Only <div>s inside: the chat bubble's `p` margin rule outranks single classes.

export interface RunPanelLine {
  text: string;
  finding?: boolean;
}

export interface ExportDesignRunPanelProps {
  plan: RunPlan;
  currentStep?: string | null;
  skipped: Record<string, string>;
  /** The lines that belong to the active step, oldest first; the latest few are shown. */
  activeLines: RunPanelLine[];
  elapsedSeconds: number;
  stepElapsedSeconds: number;
  /** What a finished step found, by step id (S5: the early ITAR answer beside the ITAR step). */
  stepMeta?: Record<string, string>;
  /** Hides the plan's foot line (once the early ITAR answer it promises is shown). */
  hideNote?: boolean;
  /** S12 R-12: the run stopped to ask. The clock reads "Paused at m:ss" and the current step waits for you. */
  paused?: boolean;
}

const MAX_LINES = 5;

const ExportDesignRunPanel: React.FC<ExportDesignRunPanelProps> = ({
  plan,
  currentStep,
  skipped,
  activeLines,
  elapsedSeconds,
  stepElapsedSeconds,
  stepMeta,
  hideNote,
  paused,
}) => {
  const statuses = runStepStatuses(plan, currentStep, skipped).map((status) =>
    paused && status === 'active' ? 'waiting' : status,
  );
  const lines = paused ? [] : activeLines.slice(-MAX_LINES);

  return (
    <section className='export-design-run' aria-live='polite' aria-label={plan.title}>
      {/* S12 R-2: the usual time sits by the title as a tag; the right-hand side is the clock alone. */}
      <div className='export-design-run-h'>
        <span className='export-design-run-title-row'>
          <span className='export-design-run-title'>{plan.title}</span>
          {plan.typical && <span className='export-design-run-typical'>{plan.typical}</span>}
        </span>
        {paused ? (
          <span className='export-design-run-time is-paused'>{`Paused at ${formatClock(elapsedSeconds)}`}</span>
        ) : (
          <span className='export-design-run-time'>
            <span className='export-design-run-clock'>{formatClock(elapsedSeconds)}</span>
          </span>
        )}
      </div>
      <ol className='export-design-run-steps'>
        {plan.steps.map((step, index) => {
          const status = statuses[index];
          const meta =
            status === 'waiting'
              ? 'waiting for you'
              : status === 'active'
              ? formatClock(stepElapsedSeconds)
              : status === 'skipped'
              ? skipped[step.id]
              : status === 'done'
              ? stepMeta?.[step.id] || step.meta || ''
              : '';
          return (
            <li key={step.id} className={`export-design-run-step is-${status}`}>
              <span className='export-design-run-mark' aria-hidden='true' />
              <span className='export-design-run-label'>
                {step.label}
                <span className='export-design-visually-hidden'>{` (${status})`}</span>
              </span>
              <span className={`export-design-run-meta${status === 'active' ? ' is-clock' : ''}`}>{meta}</span>
              {status === 'active' && step.hint && <div className='export-design-run-hint'>{step.hint}</div>}
              {status === 'active' && lines.length > 0 && (
                <div className='export-design-run-lines'>
                  {lines.map((line, i) => (
                    <div
                      key={`${i}-${line.text}`}
                      className={`export-design-run-line${line.finding ? ' is-finding' : ''}${
                        i === lines.length - 1 ? ' is-latest' : ''
                      }`}
                    >
                      <span aria-hidden='true' />
                      <div>{line.text}</div>
                    </div>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {plan.note && !hideNote && <div className='export-design-run-foot'>{plan.note}</div>}
    </section>
  );
};

export default ExportDesignRunPanel;

export interface ExportDesignStillWorkingProps {
  /** The run panel's active step, e.g. "Second review of the result". */
  stepLabel?: string;
  stepElapsedSeconds: number;
  onShowProgress?: () => void;
}

/** The Preliminary card's last row while the run goes on above it (S12 R-3, board 12 option b): from the
 * bottom of the card the run panel is often out of view, so the card says it isn't finished. */
export const ExportDesignStillWorking: React.FC<ExportDesignStillWorkingProps> = ({
  stepLabel,
  stepElapsedSeconds,
  onShowProgress,
}) => (
  <div className='export-design-live-foot' role='status'>
    <span className='export-design-live-dot' aria-hidden='true' />
    <span className='export-design-live-step'>{stepLabel ? `Still working: ${stepLabel}` : 'Still working'}</span>
    <span className='export-design-live-clock'>{formatClock(stepElapsedSeconds)}</span>
    {onShowProgress && (
      <button type='button' className='export-design-live-go' onClick={onShowProgress}>
        Show progress
      </button>
    )}
  </div>
);

export interface ExportDesignRunErrorProps {
  onRetry?: () => void;
  disabled?: boolean;
}

/** Shown when the connection closes with no answer (instead of "Thinking..." forever). */
export const ExportDesignRunError: React.FC<ExportDesignRunErrorProps> = ({ onRetry, disabled }) => (
  <section className='export-design-run is-error' role='alert'>
    <div className='export-design-run-title'>Something went wrong</div>
    <div className='export-design-run-error-text'>
      The connection closed before an answer came back. Retry sends your last message again.
    </div>
    {onRetry && (
      <div>
        <button type='button' className='standard-workflow-primary-btn' onClick={onRetry} disabled={disabled}>
          Retry
        </button>
      </div>
    )}
  </section>
);
